import * as React from "react"
import * as RechartsTypes from "recharts"
import { cn } from "../../lib/utils"

// Re-export everything from Recharts
export const {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  RadialBar,
  RadialBarChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} = RechartsTypes

// Chart container component
interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  config?: Record<string, { label?: string; color?: string }>
  children: React.ReactElement<any>
}

export function ChartContainer({
  config,
  children,
  className,
  ...props
}: ChartContainerProps) {
  return (
    <div
      data-chart=""
      className={cn(
        "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
        className
      )}
      {...props}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: generateVarStyles(config),
        }}
      />
      {children}
    </div>
  )
}

function generateVarStyles(config?: Record<string, { color?: string }>) {
  if (!config) return ""

  const styles: string[] = []
  Object.entries(config).forEach(([key, value]) => {
    if (value.color) {
      styles.push(`--color-${key}: ${value.color};`)
    }
  })

  return `:root { ${styles.join(" ")} }`
}

// Chart components
export function ChartTooltip({
  active,
  payload,
  label,
  hideLabel = false,
}: any) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-lg border bg-background p-2 shadow-sm">
      {!hideLabel && label && (
        <div className="mb-1 px-2 text-[0.70rem] uppercase text-muted-foreground">
          {label}
        </div>
      )}
      <div className="flex flex-col gap-1">
        {payload.map((item: any, index: number) => (
          <div key={index} className="flex items-center gap-2 px-2">
            <div
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <div className="flex flex-1 items-center justify-between gap-4">
              <span className="text-[0.70rem] text-muted-foreground">
                {item.name || item.dataKey}
              </span>
              <span className="text-[0.70rem] font-mono font-medium tabular-nums text-foreground">
                {item.value}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function ChartTooltipContent({
  active,
  payload,
  hideLabel = false,
  hideIndicator = false,
  indicator = "line",
  nameKey,
  labelKey,
}: any) {
  if (!active || !payload?.length) return null

  return (
    <div className="grid gap-1.5 rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-xl">
      {!hideLabel && payload[0]?.payload?.[labelKey || "name"] && (
        <div className="font-medium">
          {payload[0].payload[labelKey || "name"]}
        </div>
      )}
      <div className="grid gap-1.5">
        {payload.map((item: any, index: number) => {
          const name = nameKey ? item.payload?.[nameKey] : item.name
          return (
            <div
              key={index}
              className="flex items-center gap-1.5 font-mono font-medium tabular-nums text-foreground"
            >
              {!hideIndicator && (
                <div
                  className={cn({
                    "h-2.5 w-2.5 shrink-0 rounded-[2px] border-[--color-border]": indicator === "dot",
                    "w-1 shrink-0 rounded-full": indicator === "line",
                    "w-0 border-[1.5px] border-dashed border-l-0": indicator === "dashed",
                  })}
                  style={
                    {
                      backgroundColor: item.color,
                      borderColor: item.color,
                    } as React.CSSProperties
                  }
                />
              )}
              {name && <div className="text-muted-foreground">{name}</div>}
              <div className="ml-auto">{item.value}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}